import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Button, Paper, Typography, Grid, MenuItem,
  TableContainer, Table, TableHead, TableBody,
  IconButton, TextField
} from '@mui/material';
import {
  Breadcrumb, CTSinput, StyledTableCell, StyledTableRow
} from '../../reusableComponent/Reusables';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import dayjs from 'dayjs';
import useUnsavedChanges from '../../reusableComponent/UnsavedChanges';
import withSaveConfirmation from '../../reusableComponent/saveConfirmation';
import ReusableSnackbar from '../../reusableComponent/ReusableSnackbar';
import transferToDepotService from '../../../services/transferToDepot.Service';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

// ── Styles (same as Add_transfer_plant_entry) ─────────────────────────────────

const pageSx = {
  background: "#F4F7FB",
  minHeight: "100vh",
  padding: "20px 24px 40px",
  fontFamily: "'Inter', Arial, sans-serif",
};

const breadcrumbSx = {
  fontSize: "12px",
  color: "#718096",
  mb: 2,
  display: "flex",
  alignItems: "center",
  gap: "6px",
};

const pageHeaderSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  mb: 2.5,
};

const pageTitleSx = {
  fontSize: "16px",
  fontWeight: 700,
  color: "#000000",
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const backBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "13px",
  fontWeight: 600,
  color: "#2B6CB0",
  padding: "7px 14px",
  border: "1.5px solid #2B6CB0",
  borderRadius: "7px",
  background: "#fff",
  cursor: "pointer",
  textTransform: "none",
  "&:hover": { background: "#E6F1FB" },
};

const formCardSx = {
  background: "#fff",
  border: "1px solid #E2E8F0",
  overflow: "hidden",
  mb: 2,
};

const sectionHeaderSx = {
  background: "#1C3A5E",
  padding: "12px 22px",
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const sectionHeaderTitleSx = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#fff",
  letterSpacing: "0.3px",
};

const formBodySx = { padding: "20px 22px" };

const fieldLabelSx = {
  fontSize: "12px",
  fontWeight: 600,
  color: "#2063b6",
  mb: "4px",
  display: "flex",
  alignItems: "center",
  gap: "4px",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "13px",
    fontFamily: "'Inter', Arial, sans-serif",
    background: "#fafafa",
    height: "36px",
    "& fieldset": { borderColor: "#E2E8F0", borderWidth: "1.5px" },
    "&:hover fieldset": { borderColor: "#CBD5E0" },
    "&.Mui-focused fieldset": { borderColor: "#2B6CB0", borderWidth: "2px" },
    "&.Mui-focused": { background: "#fff" },
    "&.Mui-disabled": { background: "#F8FAFC" },
  },
  "& input": {
    color: "#1A202C",
    fontSize: "13px",
    fontFamily: "'Inter', Arial, sans-serif",
    "&::placeholder": { color: "#A0AEC0", opacity: 1 },
  },
  "& .MuiInputLabel-root": { display: "none" },
};

const readonlyInputSx = {
  ...inputSx,
  "& .MuiOutlinedInput-root": {
    ...inputSx["& .MuiOutlinedInput-root"],
    background: "#F0F4F8",
  },
};

const menuItemSx = { fontSize: "13px", fontFamily: "'Inter', Arial, sans-serif" };

const formFooterSx = {
  padding: "18px 24px",
  background: "#F8FAFC",
  borderTop: "1px solid #E2E8F0",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "10px",
};

const saveBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "7px",
  padding: "0 22px",
  height: "40px",
  background: "#4CAF50",
  color: "#fff",
  border: "none",
  fontSize: "14px",
  fontWeight: 600,
  textTransform: "none",
  borderRadius: "8px",
  fontFamily: "'Inter', Arial, sans-serif",
  "&:hover": { background: "#028b19", transform: "translateY(-1px)" },
  transition: "all 0.15s ease",
};

const cancelBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "7px",
  padding: "0 22px",
  height: "40px",
  background: "#E53E3E",
  color: "#ffffff",
  border: "none",
  "&:hover": { background: "#971f16", transform: "translateY(-1px)" },
  fontSize: "14px",
  fontWeight: 600,
  borderRadius: "8px",
  textTransform: "none",
};

// ─────────────────────────────────────────────────────────────────────────────

const AddTransferToDepot = () => {
  const navigate = useNavigate();
  const { handleSubmit } = useForm();
  const location = useLocation();
  const editData = location.state || null;

  const login_details = sessionStorage.getItem('loginUser');
  const userD = JSON.parse(login_details);
  const userInfo = userD.result.userInfo;
  const plant_id = userInfo.plant_id;
  const plant_name = userInfo.plant_name;
  const unit_id = userInfo.plant_id || userInfo.depot_id;
  const short_code = userInfo.plant_short_code || userInfo.depot_short_code;
  const user_id = userInfo.user_id;

  const [successMessage, setSuccessMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const [qcBatches, setQcBatches] = useState([]);
  const [cylinderDetails, setCylinderDetails] = useState([]);

  const initialData = useMemo(
    () => ({
      entry_id: editData?.entry_id || null,
      entry_no: editData?.entry_no || '',
      entry_date: editData?.entry_date
        ? dayjs(editData.entry_date).format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD"),
      batch_no: editData?.batch_no || '',
      qc_entry_no: editData?.qc_entry_no || '',
      from_plant_id: editData?.from_plant_id || plant_id || '',
      from_location: editData?.from_location || plant_name || '',
      to_depot_id: editData?.to_depot_id || '',
      to_location: editData?.to_location || '',
      fin_year: editData
        ? editData.fin_year
        : dayjs().month() >= 3
          ? `${dayjs().year()}-${dayjs().year() + 1}`
          : `${dayjs().year() - 1}-${dayjs().year()}`,
    }),
    [editData, plant_id, plant_name]
  );

  const [data, setData] = useState(initialData);

  const initialCylinderDetails = useMemo(
    () =>
      editData?.table_details
        ? JSON.parse(editData.table_details).map((item) => ({
            cylinder_no: item.cylinder_no || item.cyl_no || '',
            cyl_id: item.cyl_id || '',
            prod_code: item.prod_code || '',
            prod_description: item.prod_description || '',
            quality_result: item.quality_result || 'Ok',
            owner: item.owner || '',
            bar_code: item.bar_code || item.barcode || '',
          }))
        : [],
    [editData]
  );

  const isFormDirty = useMemo(
    () =>
      JSON.stringify(data) !== JSON.stringify(initialData) ||
      JSON.stringify(cylinderDetails) !== JSON.stringify(initialCylinderDetails),
    [data, cylinderDetails, initialData, initialCylinderDetails]
  );

  const { confirmNavigation } = useUnsavedChanges(isFormDirty);

  // ── Fetch QC-passed batches + generate entry_no ───────────────────────────
  useEffect(() => {
    async function fetchData() {
      try {
        const batches = await transferToDepotService.getQCPassedBatches();
        setQcBatches(Array.isArray(batches) ? batches : []);

        // Set initial cylinders if editing
        if (editData) {
          setCylinderDetails(initialCylinderDetails);
          return;
        }

        // Auto-generate entry_no
        const list = await transferToDepotService.getTransferToDepotList({ page: 0, size: 5, searchKey: '' });
        const yr = dayjs();
        const finYear =
          yr.month() >= 3
            ? `${String(yr.year()).slice(-2)}-${String(yr.year() + 1).slice(-2)}`
            : `${String(yr.year() - 1).slice(-2)}-${String(yr.year()).slice(-2)}`;

        const sameYearEntries = (list || []).filter((d) => d.entry_no?.includes(finYear));
        const lastEntryNo = sameYearEntries?.[0]?.entry_no ?? '';
        const parts = String(lastEntryNo).split('/');
        const lastSeq = parseInt(parts[parts.length - 1]) || 0;
        const nextSeq = lastSeq + 1;
        const formattedEntryNo = `TD/${short_code}/${finYear}/${String(nextSeq).padStart(4, '0')}`;

        setData((prev) => ({
          ...prev,
          entry_no: formattedEntryNo,
          fin_year:
            yr.month() >= 3
              ? `${yr.year()}-${yr.year() + 1}`
              : `${yr.year() - 1}-${yr.year()}`,
        }));
      } catch (error) {
        setWarningMessage('Error fetching data.');
        console.error(error);
      }
    }
    fetchData();
  }, []);

  // ── Handle batch selection ────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'batch_no') {
      const selected = qcBatches.find((b) => b.batch_no === value);
      if (selected) {
        let cyls = [];
        try {
          cyls = typeof selected.table_details === 'string'
            ? JSON.parse(selected.table_details)
            : selected.table_details || [];
          // Only QC-passed cylinders
         cyls = cyls.filter((c) => c.quality_result === 'Ok');
const mappedCyls = cyls.map(c => ({
  cylinder_no: c.cylinder_no || c.cyl_no || '',
  cyl_id: c.cyl_id || '',
  prod_code: c.prod_code || '',
  prod_description: c.prod_description || '',
  quality_result: c.quality_result || 'Ok',
  owner: c.owner || '',
  bar_code: c.bar_code || c.barcode || c.tag_no || '',
}));
setCylinderDetails(mappedCyls);
        } catch (e) {
          console.error('Error parsing table_details:', e);
        }
        setData((prev) => ({
          ...prev,
          batch_no: value,
          qc_entry_no: selected.qc_entry_no || '',
          from_plant_id: selected.from_plant_id || plant_id || '',
          from_location: selected.from_plant_name || plant_name || '',
          to_depot_id: selected.to_depot_id || '',
          to_location: selected.to_depot_name || '',
        }));
        
      } else {
        setData((prev) => ({ ...prev, batch_no: value }));
        setCylinderDetails([]);
      }
      return;
    }

    if (name === 'entry_date') {
      const selectedDate = dayjs(value);
      if (selectedDate.isValid()) {
        const month = selectedDate.month();
        const year = selectedDate.year();
        const financialYear = month >= 3 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
        setData((prev) => ({ ...prev, entry_date: selectedDate.format('YYYY-MM-DD'), fin_year: financialYear }));
      } else {
        setData((prev) => ({ ...prev, entry_date: '' }));
        setWarningMessage('Invalid date selected.');
      }
      return;
    }

    setData((prev) => ({ ...prev, [name]: value }));
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const onSubmit = async (formData) => {
    if (!data.batch_no) {
      setWarningMessage('Please select a Batch No.');
      return;
    }
    if (!data.to_depot_id) {
      setWarningMessage('No depot assigned for this batch. Please check QC entry.');
      return;
    }
    if (cylinderDetails.length === 0) {
      setWarningMessage('No QC-passed cylinders found for this batch.');
      return;
    }

    const payload = {
      ...data,
      table_details: JSON.stringify(cylinderDetails),
      created_by: user_id,
      modified_by: editData ? user_id : null,
      created_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      modified_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      unit_id,
      short_code,
      changedUpdatedValue: 'edit',
    };

    if (editData) {
      await transferToDepotService.editDeleteTransferToDepot(payload);
    } else {
      await transferToDepotService.addTransferToDepot(payload);
    }
  };

  const handleSubmitWithConfirmation = withSaveConfirmation(onSubmit);
  const handleCancel = () => confirmNavigation(-1);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={pageSx}>

        {/* Breadcrumb */}
        <Box sx={breadcrumbSx}>
          <span>Dashboard</span> /{' '}
          <span style={{ color: '#2B6CB0', fontWeight: 600 }}>Transfer to Depot Entry</span>
        </Box>

        {/* Page Header */}
        <Box sx={pageHeaderSx}>
          <Box sx={pageTitleSx}>
            <SwapHorizIcon sx={{ fontSize: 20, color: '#1C3A5E' }} />
            {editData ? 'UPDATE' : 'ADD'} TRANSFER TO DEPOT ENTRY
          </Box>
          <Button onClick={() => navigate(-1)} sx={backBtnSx}>
            <ArrowBackIcon sx={{ fontSize: 16 }} /> Back
          </Button>
        </Box>

        <form
          onSubmit={handleSubmit(() =>
            handleSubmitWithConfirmation(data, setSuccessMessage, setWarningMessage, navigate, editData)
          )}
        >

          {/* ── Entry Details Card ── */}
          <Box sx={formCardSx}>
            <Box sx={sectionHeaderSx}>
              <SwapHorizIcon sx={{ fontSize: 18, color: '#fff' }} />
              <Typography sx={sectionHeaderTitleSx}>TRANSFER TO DEPOT DETAILS</Typography>
            </Box>

            <Box sx={formBodySx}>
              <Grid container sx={{ border: '1px solid #E2E8F0', borderRadius: '10px', overflow: 'hidden' }}>

                {/* ════ LEFT — Batch No ════ */}
                <Grid
                  item xs={12} md={6}
                  sx={{
                    p: 2.5,
                    borderBottom: { xs: '2px dashed #CBD5E0', md: 'none' },
                    borderRight: { md: '1px solid #E2E8F0' },
                  }}
                >
                  <Grid container spacing={1.5}>

                    {/* Batch No */}
                    <Grid item xs={12} sm={7}>
                      <Typography sx={fieldLabelSx}>
                        Batch No <span style={{ color: '#E53E3E' }}>*</span>
                      </Typography>
                      <CTSinput
                        select
                        fullWidth
                        size="small"
                        name="batch_no"
                        value={data.batch_no || ''}
                        onChange={handleChange}
                        sx={inputSx}
                      >
                        {qcBatches.length > 0 ? (
                          qcBatches.map((b) => (
                            <MenuItem key={b.batch_no} value={b.batch_no} sx={menuItemSx}>
                              {b.batch_no}
                              
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem value="" disabled sx={menuItemSx}>
                            No QC-passed batches available
                          </MenuItem>
                        )}
                      </CTSinput>
                    </Grid>

                  </Grid>
                </Grid>

                {/* ════ RIGHT — Entry No + Entry Date ════ */}
                <Grid
                  item xs={12} md={6}
                  sx={{ p: 2.5, background: '#FAFBFC', pl: 3.5 }}
                >
                  <Grid container spacing={1.5}>

                    {/* Entry No */}
                    <Grid item xs={12} sm={5}>
                      <Typography sx={fieldLabelSx}>Entry No</Typography>
                      <TextField
                        size="small"
                        fullWidth
                        name="entry_no"
                        value={data.entry_no}
                        sx={readonlyInputSx}
                        InputProps={{ readOnly: true }}
                        placeholder="Auto-generated"
                      />
                    </Grid>

                    {/* Entry Date */}
                    <Grid item xs={12} sm={5}>
                      <Typography sx={fieldLabelSx}>
                        Entry Date <span style={{ color: '#E53E3E' }}>*</span>
                      </Typography>
                      <DatePicker
                        format="DD/MM/YYYY"
                        value={data.entry_date ? dayjs(data.entry_date) : dayjs()}
                        onChange={(newValue) =>
                          handleChange({
                            target: {
                              name: 'entry_date',
                              value: newValue ? newValue.format('YYYY-MM-DD') : '',
                            },
                          })
                        }
                        slotProps={{
                          textField: {
                            size: 'small',
                            fullWidth: true,
                            required: true,
                            sx: inputSx,
                          },
                        }}
                      />
                    </Grid>

                  </Grid>
                </Grid>

              </Grid>
            </Box>
          </Box>

          {/* ── Cylinder Table Card ── */}
          <Box sx={formCardSx}>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <tr>
                    {[
                      { label: 'Sr No', align: 'center' },
                      { label: 'Cylinder No', align: 'left' },
                      { label: 'Tag No', align: 'left' },
                      // { label: 'Product', align: 'left' },
                      { label: 'QC Result', align: 'center' },
                      { label: 'Owner', align: 'left' },
                    ].map(({ label, align }) => (
                      <th
                        key={label}
                        style={{
                          background: '#1C3A5E',
                          color: '#fff',
                          fontSize: '12px',
                          fontWeight: 600,
                          fontFamily: "'Inter', Arial, sans-serif",
                          padding: '10px 12px',
                          borderRight: '1px solid rgba(255,255,255,0.1)',
                          whiteSpace: 'nowrap',
                          textAlign: align,
                        }}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </TableHead>
                <TableBody>
                  {cylinderDetails.map((row, index) => (
                    <tr
                      key={index}
                      style={{
                        background: index % 2 === 0 ? '#fff' : '#F8FAFC',
                        borderBottom: '1px solid #E2E8F0',
                      }}
                    >
                      {/* Sr No */}
                      <td style={{ padding: '6px 12px', fontSize: '12px', fontFamily: "'Inter', Arial, sans-serif", color: '#2D3748', textAlign: 'center' }}>
                        {index + 1}
                      </td>

                      {/* Cylinder No */}
                      <td style={{ padding: '4px 8px', minWidth: '140px' }}>
                        <TextField
                          fullWidth size="small"
                          value={row.cylinder_no || ''}
                          InputProps={{ readOnly: true }}
                          sx={{ ...inputSx, '& .MuiOutlinedInput-root': { ...inputSx['& .MuiOutlinedInput-root'], height: '30px', '& fieldset': { border: 'none' } } }}
                        />
                      </td>

                      {/* Bar Code */}
                      <td style={{ padding: '4px 8px', minWidth: '120px' }}>
                        <TextField
                          fullWidth size="small"
                          value={row.bar_code || ''}
                          InputProps={{ readOnly: true }}
                          sx={{ ...inputSx, '& .MuiOutlinedInput-root': { ...inputSx['& .MuiOutlinedInput-root'], height: '30px', '& fieldset': { border: 'none' } } }}
                        />
                      </td>

                      {/* Product */}
                      {/* <td style={{ padding: '4px 8px', minWidth: '140px' }}>
                        <TextField
                          fullWidth size="small"
                          value={row.prod_code ? `${row.prod_code}${row.prod_description ? ' - ' + row.prod_description : ''}` : row.prod_description || ''}
                          InputProps={{ readOnly: true }}
                          sx={{ ...inputSx, '& .MuiOutlinedInput-root': { ...inputSx['& .MuiOutlinedInput-root'], height: '30px', '& fieldset': { border: 'none' } } }}
                        />
                      </td> */}

                      {/* QC Result */}
                      <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block',
                          fontSize: '11px',
                          fontWeight: 700,
                          color: '#2e7d32',
                          background: '#e8f5e9',
                          borderRadius: '4px',
                          padding: '2px 10px',
                          fontFamily: "'Inter', Arial, sans-serif",
                        }}>
                          {row.quality_result || 'Ok'}
                        </span>
                      </td>

                      {/* Owner */}
                      <td style={{ padding: '4px 8px', minWidth: '100px' }}>
                        <TextField
                          fullWidth size="small"
                          value={row.owner || ''}
                          InputProps={{ readOnly: true }}
                          sx={{ ...inputSx, '& .MuiOutlinedInput-root': { ...inputSx['& .MuiOutlinedInput-root'], height: '30px', '& fieldset': { border: 'none' } } }}
                        />
                      </td>
                    </tr>
                  ))}

                  {cylinderDetails.length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        style={{
                          padding: '32px',
                          textAlign: 'center',
                          color: '#A0AEC0',
                          fontSize: '13px',
                          fontFamily: "'Inter', Arial, sans-serif",
                        }}
                      >
                        No cylinders added. Select a QC-passed Batch No to auto-populate.
                      </td>
                    </tr>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Footer Buttons */}
            <Box sx={formFooterSx}>
              <Button sx={cancelBtnSx} onClick={handleCancel}>Cancel</Button>
              <Button type="submit" sx={saveBtnSx}>
                <SaveIcon sx={{ fontSize: 18 }} />
                {editData ? 'Update' : 'Save'}
              </Button>
            </Box>
          </Box>

        </form>

        {/* Snackbars */}
        <ReusableSnackbar
          open={!!successMessage}
          onClose={(event, reason) => { if (reason !== 'clickaway') setSuccessMessage(''); }}
          message={successMessage}
          autoHideDuration={2000}
          vertical="top"
          horizontal="right"
        />
        <ReusableSnackbar
          open={!!warningMessage}
          onClose={(event, reason) => { if (reason !== 'clickaway') setWarningMessage(''); }}
          message={warningMessage}
          autoHideDuration={4000}
          vertical="top"
          horizontal="right"
          severity="warning"
        />
      </Box>
    </LocalizationProvider>
  );
};

export default AddTransferToDepot;
